import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {useTranslation} from "react-i18next";
import type {InsightDistributionValueV4} from "../../models/crm-v4";
import type {InsightsLoading} from "../../kit/insightsLoading";
import CrmBucketsCard, {bucketRows} from "./CrmBucketsCard";

interface GuestValueCardProps {
    items: InsightDistributionValueV4[];
    loading?: InsightsLoading;
    comparing: boolean;
}

const CONCENTRATION_LABELS: Record<string, string> = {
    TOP_10: "Top 10%",
    P10_20: "10–20%",
    P20_30: "20–30%",
    P30_40: "30–40%",
    P40_50: "40–50%",
    P50_60: "50–60%",
    P60_70: "60–70%",
    P70_80: "70–80%",
    P80_90: "80–90%",
    LAST_10: "Last 10%",
};

export function RevenueConcentrationCard({items, loading, comparing}: GuestValueCardProps) {
    const {t, i18n} = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language;
    const countFormatter = new Intl.NumberFormat(locale, {maximumFractionDigits: 0});
    const percent = (value: number) => `${countFormatter.format(value)}%`;

    return (
        <CrmBucketsCard
            title={t("Revenue concentration")}
            hint={t("Share of revenue by guest value group.")}
            rows={bucketRows(items, key => t(CONCENTRATION_LABELS[key] ?? key))}
            loading={loading}
            comparing={comparing}
            format={percent}
            formatTick={percent}
            valueLabel={t("Share")}
            empty={t("No data in the selected period")}
        />
    );
}

export function GuestsBySpendCard({items, loading, comparing}: GuestValueCardProps) {
    const {t, i18n} = useTranslation();
    const currency = useOrganization()?.currencyCode || "EUR";
    const locale = i18n.resolvedLanguage || i18n.language;
    const countFormatter = new Intl.NumberFormat(locale, {maximumFractionDigits: 0});
    const compactCountFormatter = new Intl.NumberFormat(locale, {notation: "compact", maximumFractionDigits: 1});
    const compactCurrencyFormatter = new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        notation: "compact",
        maximumFractionDigits: 1,
    });
    const money = (value: number) => compactCurrencyFormatter.format(value);
    const spendLabels: Record<string, string> = {
        UNDER_500: `<${money(500)}`,
        FROM_500_TO_999: `${money(500)}–${money(1_000)}`,
        FROM_1000_TO_2499: `${money(1_000)}–${money(2_500)}`,
        FROM_2500_TO_4999: `${money(2_500)}–${money(5_000)}`,
        FROM_5000_TO_14999: `${money(5_000)}–${money(15_000)}`,
        FROM_15000: `${money(15_000)}+`,
    };

    return (
        <CrmBucketsCard
            title={t("Guests by spend")}
            hint={t("Guests grouped by lifetime spend.")}
            rows={bucketRows(items, key => spendLabels[key] ?? key)}
            loading={loading}
            comparing={comparing}
            format={value => `${countFormatter.format(value)} ${t("guests")}`}
            formatTick={value => compactCountFormatter.format(value)}
            valueLabel={t("Guests")}
            empty={t("No data in the selected period")}
        />
    );
}
